import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, Target, Clock, Award, Brain, AlertCircle, BookOpen, 
  Zap, Trophy, Calendar, BarChart3, PieChart, ArrowUp, ArrowDown,
  Sun, Moon, Sunrise, Sunset, Flame, CheckCircle2, XCircle, Crown,
  Lightbulb, RefreshCw, Activity, Timer, Eye, RotateCcw, Gauge,
  TrendingDown, Crosshair, GitBranch, Layers, Users,
  SkipForward, PenTool, Battery, Coffee, Focus, Hourglass, 
  LineChart, Scale, Radar, Map, CalendarCheck, Hash, Shield, Star,
  ArrowLeft, ArrowRight, Check, X, Settings, Flag, Minus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import LoadingSpinner from '../components/LoadingSpinner';


const AdvancedAnalytics = () => {
  const [basicAnalytics, setBasicAnalytics] = useState(null);
  const [deepAnalytics, setDeepAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deepLoading, setDeepLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeCategory, setActiveCategory] = useState('Performance');
  const [testHistory, setTestHistory] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testDetails, setTestDetails] = useState(null);
  const [loadingTests, setLoadingTests] = useState(false);
  const [loadingTestDetails, setLoadingTestDetails] = useState(false);
  const navigate = useNavigate();

  const tabCategories = [
    {
      name: 'Performance',
      tabs: [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        { id: 'readiness', label: 'Exam Ready', icon: Shield },
        { id: 'benchmarks', label: 'Ranking', icon: Users },
      ]
    },
    {
      name: 'Analysis',
      tabs: [
        { id: 'cognitive', label: 'Cognitive', icon: Brain },
        { id: 'time', label: 'Time', icon: Timer },
        { id: 'behavior', label: 'Behavior', icon: Activity },
      ]
    },
    {
      name: 'Insights',
      tabs: [
        { id: 'topics', label: 'Topics', icon: Map },
        { id: 'mistakes', label: 'Mistakes', icon: XCircle },
        { id: 'predictions', label: 'Predictions', icon: TrendingUp },
      ]
    },
    {
      name: 'Test Results',
      tabs: [
        { id: 'test-history', label: 'History', icon: Trophy },
      ]
    },
    {
      name: 'Patterns',
      tabs: [
        { id: 'peak', label: 'Peak Hours', icon: Sun },
        { id: 'study', label: 'Study Habits', icon: CalendarCheck },
      ]
    }
  ];

  useEffect(() => {
    loadAnalytics();
  }, []);

  useEffect(() => {
    if (activeTab === 'test-history') {
      loadTestHistory();
    }
  }, [activeTab]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [basicRes, deepRes] = await Promise.all([
        api.get('/analytics/dashboard').catch(() => null),
        api.get('/analytics/deep').catch(() => null)
      ]);

      if (basicRes?.data) setBasicAnalytics(basicRes.data);
      if (deepRes?.data && !deepRes.data.error) setDeepAnalytics(deepRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Analytics is a premium feature');
        navigate('/subscription');
      } else {
        toast.error('Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadDeepAnalytics = async () => {
    setDeepLoading(true);
    try {
      const response = await api.get('/analytics/deep');
      if (response.data && !response.data.error) {
        setDeepAnalytics(response.data);
        toast.success('Deep analytics loaded!');
      } else {
        toast.info(response.data.error || 'Not enough data for deep analytics');
      }
    } catch (error) {
      toast.error('Failed to load deep analytics');
    } finally {
      setDeepLoading(false);
    }
  };

  const loadTestHistory = async () => {
    setLoadingTests(true);
    try {
      const response = await api.get('/tests/history');
      setTestHistory(response.data || []);
    } catch (error) {
      toast.error('Failed to load test history');
      setTestHistory([]);
    } finally {
      setLoadingTests(false);
    }
  };

  const loadTestDetails = async (testId) => {
    setLoadingTestDetails(true);
    setSelectedTest(testId);
    try {
      const response = await api.get(`/tests/${testId}/results`);
      setTestDetails(response.data);
    } catch (error) {
      toast.error('Failed to load test details');
      setTestDetails(null);
    } finally {
      setLoadingTestDetails(false);
    }
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return 'text-green-600 dark:text-green-400';
    if (accuracy >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getAccuracyBg = (accuracy) => {
    if (accuracy >= 80) return 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800';
    if (accuracy >= 60) return 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800';
    return 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800';
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner size="xl" text="Analyzing Your Performance" fullScreen={false} />
      </Layout>
    );
  }

  if (!basicAnalytics) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="bg-red-50 dark:bg-red-950/30 rounded-full p-6 mb-6">
            <AlertCircle className="text-red-600 dark:text-red-400" size={64} aria-hidden="true" />
          </div>
          <h2 className="text-3xl font-bold text-foreground dark:text-white mb-3 text-center">No Data Available</h2>
          <p className="text-muted-foreground mb-8 text-center max-w-md">
            Complete some tests to see your detailed analytics and performance insights
          </p>
          <Button onClick={() => navigate('/practice')} size="lg" className="gap-2">
            <BookOpen size={20} aria-hidden="true" />
            Start Practicing
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Header */}
        <header className="mb-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-start gap-4">
              
              <div>
                <h1 className="text-3xl lg:text-4xl font-heading font-bold text-foreground dark:text-white mb-2">
                  Advanced Analytics
                </h1>
                <p className="text-muted-foreground flex items-center gap-2 text-sm lg:text-base">
                  <LineChart className="text-blue-500" size={18} aria-hidden="true" />
                  Detailed performance insights
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={loadDeepAnalytics} 
                variant="outline" 
                className="gap-2" 
                disabled={deepLoading}
                aria-label="Run deep analytics scan"
              >
                <Radar size={18} className={deepLoading ? 'animate-spin' : ''} aria-hidden="true" />
                {deepLoading ? 'Analyzing...' : 'Deep Scan'}
              </Button>
              <Button 
                onClick={loadAnalytics} 
                variant="outline" 
                className="gap-2"
                aria-label="Refresh analytics data"
              >
                <RefreshCw size={18} aria-hidden="true" />
                Refresh
              </Button>
            </div>
          </div>
        </header>

        {/* iOS-Style Segmented Control Navigation */}
        <nav className="mb-8 space-y-4" aria-label="Analytics sections">
          {/* Category Selector - iOS Style Segmented Control */}
          <div className="bg-muted/50 p-1.5 rounded-xl inline-flex gap-1 w-full max-w-2xl mx-auto">
            {tabCategories.map(category => {
              const isActive = activeCategory === category.name;
              return (
                <button
                  key={category.name}
                  onClick={() => {
                    setActiveCategory(category.name);
                    setActiveTab(category.tabs[0].id);
                  }}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all ${
                    isActive 
                      ? 'bg-white dark:bg-gray-800 text-foreground dark:text-white shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  aria-pressed={isActive}
                  aria-label={`View ${category.name} analytics`}
                >
                  {category.name}
                </button>
              );
            })}
          </div>

          {/* Sub-tabs for selected category */}
          <div className="flex justify-center">
            <div className="inline-flex gap-2 bg-card p-2 rounded-xl border border-border">
              {tabCategories
                .find(cat => cat.name === activeCategory)
                ?.tabs.map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                        isActive
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                      aria-pressed={isActive}
                      aria-label={`View ${tab.label}`}
                    >
                      <Icon size={18} aria-hidden="true" />
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                    </button>
                  );
                })}
            </div>
          </div>
        </nav>

        {/* Data Quality Banners */}
        {deepAnalytics?.very_limited_data && (
          <div role="alert" className="mb-6">
            <Card className="p-5 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-xl">
              <div className="flex items-start gap-4">
                <AlertCircle className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-1" size={28} aria-hidden="true" />
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-900 dark:text-amber-100 text-lg mb-1">Limited Data Available</h3>
                  <p className="text-amber-800 dark:text-amber-200">
                    Complete more tests for accurate insights. Current analysis based on {deepAnalytics.total_responses_analyzed} responses.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}
        {deepAnalytics?.limited_data && !deepAnalytics?.very_limited_data && (
          <div role="status" className="mb-6">
            <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-xl">
              <div className="flex items-start gap-3">
                <TrendingUp className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" size={22} aria-hidden="true" />
                <p className="text-blue-800 dark:text-blue-200">
                  {deepAnalytics.data_quality_message} ({deepAnalytics.total_responses_analyzed} responses analyzed)
                </p>
              </div>
            </Card>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Hero Stats */}
            <section aria-labelledby="hero-stats-heading">
              <h2 id="hero-stats-heading" className="sr-only">Key Performance Metrics</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <Card className="p-6 lg:p-8 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl shadow-lg">
                  <Target className="mb-4" size={32} aria-hidden="true" />
                  <p className="text-blue-100 text-sm font-medium mb-1">Overall Accuracy</p>
                  <p className="text-4xl lg:text-5xl font-bold" aria-label={`${basicAnalytics.overall_accuracy} percent accuracy`}>
                    {basicAnalytics.overall_accuracy}%
                  </p>
                </Card>
                
                <Card className="p-6 lg:p-8 bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-2xl shadow-lg">
                  <CheckCircle2 className="mb-4" size={32} aria-hidden="true" />
                  <p className="text-green-100 text-sm font-medium mb-1">Correct Answers</p>
                  <div>
                    <p className="text-4xl lg:text-5xl font-bold" aria-label={`${basicAnalytics.total_correct} correct answers`}>
                      {basicAnalytics.total_correct}
                    </p>
                    <p className="text-green-200 text-sm mt-1">of {basicAnalytics.total_questions_attempted}</p>
                  </div>
                </Card>
                
                <Card className="p-6 lg:p-8 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-2xl shadow-lg">
                  <BookOpen className="mb-4" size={32} aria-hidden="true" />
                  <p className="text-purple-100 text-sm font-medium mb-1">Tests Completed</p>
                  <p className="text-4xl lg:text-5xl font-bold" aria-label={`${basicAnalytics.total_tests} tests completed`}>
                    {basicAnalytics.total_tests}
                  </p>
                </Card>
                
                <Card className="p-6 lg:p-8 bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-2xl shadow-lg">
                  <Clock className="mb-4" size={32} aria-hidden="true" />
                  <p className="text-orange-100 text-sm font-medium mb-1">Study Time</p>
                  <p className="text-4xl lg:text-5xl font-bold" aria-label={`${basicAnalytics.total_study_time_hours} hours of study time`}>
                    {basicAnalytics.total_study_time_hours}h
                  </p>
                </Card>
              </div>
            </section>

            {/* Quick Insights from Deep Analytics */}
            {deepAnalytics && (
              <section aria-labelledby="quick-insights-heading">
                <h2 id="quick-insights-heading" className="text-2xl font-bold text-foreground dark:text-white mb-6">Quick Insights</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                  {/* Cognitive Score */}
                  <Card className="p-6 lg:p-8 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 rounded-2xl border-2 border-indigo-200 dark:border-indigo-700 shadow-md">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <Brain className="text-white" size={24} />
                      </div>
                      <h3 className="font-bold text-foreground dark:text-white text-lg">Fatigue Index</h3>
                    </div>
                    <p className="text-4xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">
                      {deepAnalytics.cognitive_analysis?.fatigue_index || 0}
                      <span className="text-xl text-muted-foreground ml-1">/100</span>
                    </p>
                    <p className="text-muted-foreground">
                      {deepAnalytics.cognitive_analysis?.fatigue_level || 'Unknown'} fatigue level
                    </p>
                  </Card>

                  {/* Learning Velocity */}
                  <Card className="p-6 lg:p-8 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-2xl border-2 border-green-200 dark:border-green-700 shadow-md">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <TrendingUp className="text-white" size={24} />
                      </div>
                      <h3 className="font-bold text-foreground dark:text-white text-lg">Learning Velocity</h3>
                    </div>
                    <p className="text-4xl font-bold text-green-600 dark:text-green-400 mb-2">
                      {deepAnalytics.learning_velocity?.velocity_score > 0 ? '+' : ''}
                      {deepAnalytics.learning_velocity?.velocity_score || 0}%
                      <span className="text-xl text-muted-foreground ml-1">/week</span>
                    </p>
                    <p className="text-muted-foreground">
                      {deepAnalytics.learning_velocity?.velocity_label || 'Calculating...'}
                    </p>
                  </Card>

                  {/* Percentile Rank */}
                  <Card className="p-6 lg:p-8 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-950/30 dark:to-orange-950/30 rounded-2xl border-2 border-yellow-200 dark:border-yellow-700 shadow-md">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-yellow-600 rounded-xl flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <Trophy className="text-white" size={24} />
                      </div>
                      <h3 className="font-bold text-foreground dark:text-white text-lg">Your Rank</h3>
                    </div>
                    <p className="text-4xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">
                      Top {(100 - (deepAnalytics.benchmarking?.overall_percentile || 50)).toFixed(0)}%
                    </p>
                    <p className="text-muted-foreground">
                      Rank #{deepAnalytics.benchmarking?.rank_position || '?'} of {deepAnalytics.benchmarking?.total_users_compared || '?'}
                    </p>
                  </Card>
                </div>
              </section>
            )}

            {/* AI Insights */}
            {basicAnalytics.insights?.length > 0 && (
              <section aria-labelledby="ai-insights-heading">
                <Card className="p-6 lg:p-8 bg-card rounded-2xl border-2 border-border shadow-md">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl flex items-center justify-center flex-shrink-0" aria-hidden="true">
                      <Lightbulb className="text-yellow-600 dark:text-yellow-400" size={28} />
                    </div>
                    <h2 id="ai-insights-heading" className="text-xl lg:text-2xl font-bold text-foreground dark:text-white">AI Insights</h2>
                  </div>
                  <ul className="space-y-4">
                    {basicAnalytics.insights.map((insight, i) => (
                      <li key={i} className="p-5 bg-muted/50 rounded-xl border border-muted">
                        <p className="text-foreground dark:text-white leading-relaxed">{insight}</p>
                      </li>
                    ))}
                  </ul>
                </Card>
              </section>
            )}

            {/* Improvement Banner */}
            {basicAnalytics.improvement_percentage !== 0 && (
              <section aria-labelledby="improvement-heading">
                <Card className={`p-6 lg:p-8 rounded-2xl border-2 shadow-md ${basicAnalytics.improvement_percentage > 0 
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-300 dark:border-green-700' 
                  : 'bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/30 border-red-300 dark:border-red-700'}`}>
                  <div className="flex items-center gap-6">
                    {basicAnalytics.improvement_percentage > 0 ? (
                      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <ArrowUp className="text-white" size={32} />
                      </div>
                    ) : (
                      <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <ArrowDown className="text-white" size={32} />
                      </div>
                    )}
                    <div>
                      <h2 id="improvement-heading" className="text-xl lg:text-2xl font-bold text-foreground dark:text-white mb-2">
                        {basicAnalytics.improvement_percentage > 0 ? "You're Improving!" : 'Focus Needed'}
                      </h2>
                      <p className="text-muted-foreground text-base lg:text-lg">
                        Your recent performance is <span className={`font-semibold ${basicAnalytics.improvement_percentage > 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                          {Math.abs(basicAnalytics.improvement_percentage)}%
                        </span> {basicAnalytics.improvement_percentage > 0 ? 'better' : 'lower'} than before
                      </p>
                    </div>
                  </div>
                </Card>
              </section>
            )}
          </div>
        )}

        {/* Cognitive Tab */}
        {activeTab === 'cognitive' && (
          <div className="space-y-8">
            {!deepAnalytics?.cognitive_analysis ? (
              <Card className="p-12 lg:p-16 text-center bg-card rounded-2xl border-2 border-border shadow-md">
                
                <h3 className="text-2xl lg:text-3xl font-bold text-foreground dark:text-white mb-3">Cognitive Analysis</h3>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto text-lg">
                  Run a deep scan to analyze your cognitive patterns and mental performance
                </p>
                <Button onClick={loadDeepAnalytics} size="lg" className="gap-2" disabled={deepLoading}>
                  <Radar size={20} className={deepLoading ? 'animate-spin' : ''} aria-hidden="true" />
                  {deepLoading ? 'Analyzing...' : 'Run Deep Scan'}
                </Button>
              </Card>
            ) : (
              <>
                {/* Fatigue Analysis */}
                <section aria-labelledby="fatigue-heading">
                  <Card className="p-6 lg:p-8 bg-card rounded-2xl border-2 border-border shadow-md">
                    <h2 id="fatigue-heading" className="text-xl lg:text-2xl font-bold text-foreground dark:text-white mb-8 flex items-center gap-3">
                      <Battery className="text-orange-500" size={28} aria-hidden="true" />
                      Cognitive Fatigue Analysis
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                      <div className="text-center p-8 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/30 rounded-2xl border-2 border-orange-200 dark:border-orange-700">
                        <p className="text-muted-foreground font-medium mb-3">Fatigue Index</p>
                        <p className="text-5xl lg:text-6xl font-bold text-orange-600 dark:text-orange-400 mb-3">
                          {deepAnalytics.cognitive_analysis.fatigue_index}
                        </p>
                        <Badge className={`text-base px-4 py-1 ${
                          deepAnalytics.cognitive_analysis.fatigue_level === 'Low' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          deepAnalytics.cognitive_analysis.fatigue_level === 'Moderate' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                          'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {deepAnalytics.cognitive_analysis.fatigue_level} Fatigue
                        </Badge>
                      </div>
                      
                      <div className="text-center p-8 bg-muted/50 rounded-2xl border-2 border-muted">
                        <p className="text-muted-foreground font-medium mb-3">First Half Accuracy</p>
                        <p className="text-4xl lg:text-5xl font-bold text-green-600 dark:text-green-400 mb-2">
                          {deepAnalytics.cognitive_analysis.first_half_accuracy}%
                        </p>
                        <p className="text-sm text-muted-foreground">Questions 1-50%</p>
                      </div>
                      
                      <div className="text-center p-8 bg-muted/50 rounded-2xl border-2 border-muted">
                        <p className="text-muted-foreground font-medium mb-3">Second Half Accuracy</p>
                        <p className="text-4xl lg:text-5xl font-bold text-red-600 dark:text-red-400 mb-2">
                          {deepAnalytics.cognitive_analysis.second_half_accuracy}%
                        </p>
                        <p className="text-sm text-muted-foreground">Questions 51-100%</p>
                      </div>
                    </div>

                    <div className="p-6 bg-blue-50 dark:bg-blue-950/30 rounded-xl border-2 border-blue-200 dark:border-blue-700" role="note">
                      <p className="flex items-start gap-3 text-base lg:text-lg">
                        <Lightbulb className="text-blue-600 dark:text-blue-400 shrink-0 mt-1" size={24} aria-hidden="true" />
                        <span className="text-foreground dark:text-white leading-relaxed">
                          {deepAnalytics.cognitive_analysis.recommendation}
                        </span>
                      </p>
                    </div>
                  </Card>
                </section>

                {/* Concentration Score */}
                <section aria-labelledby="concentration-heading">
                  <Card className="p-6 lg:p-8 bg-card rounded-2xl border-2 border-border shadow-md">
                    <h2 id="concentration-heading" className="text-xl lg:text-2xl font-bold text-foreground dark:text-white mb-8 flex items-center gap-3">
                      <Crosshair className="text-purple-500" size={28} aria-hidden="true" />
                      Concentration Analysis
                    </h2>
                    
                    <div className="flex flex-col md:flex-row items-center gap-8 mb-8">
                      <div className="relative w-40 h-40 flex-shrink-0">
                        <svg className="w-full h-full transform -rotate-90" role="img" aria-label={`Concentration score: ${deepAnalytics.cognitive_analysis.concentration_score} percent`}>
                          <circle cx="80" cy="80" r="70" fill="none" stroke="currentColor" strokeWidth="14" className="text-muted" />
                          <circle 
                            cx="80" cy="80" r="70" fill="none" stroke="currentColor" strokeWidth="14" 
                            strokeDasharray={`${deepAnalytics.cognitive_analysis.concentration_score * 4.4} 440`}
                            className="text-purple-500"
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-3xl font-bold text-foreground dark:text-white">
                            {deepAnalytics.cognitive_analysis.concentration_score}%
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 text-center md:text-left">
                        <p className="text-xl lg:text-2xl font-semibold text-foreground dark:text-white mb-3">
                          {deepAnalytics.cognitive_analysis.concentration_score >= 80 ? 'Excellent Focus' :
                           deepAnalytics.cognitive_analysis.concentration_score >= 60 ? 'Good Focus' : 'Needs Improvement'}
                        </p>
                        <p className="text-muted-foreground text-base lg:text-lg leading-relaxed">
                          Your accuracy consistency across question positions indicates how well you maintain focus throughout tests.
                        </p>
                      </div>
                    </div>

                    {/* Accuracy by Position */}
                    <div>
                      <h3 className="font-semibold text-foreground dark:text-white mb-4 text-lg">Accuracy by Question Position</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                        {Object.entries(deepAnalytics.cognitive_analysis.position_accuracy || {}).map(([pos, acc]) => (
                          <div key={pos} className={`text-center p-4 rounded-xl border-2 ${getAccuracyBg(acc)}`}>
                            <p className="text-sm text-muted-foreground mb-1">Question {pos}</p>
                            <p className={`text-2xl font-bold ${getAccuracyColor(acc)}`}>{acc}%</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                </section>
              </>
            )}
          </div>
        )}

        {/* Time Analysis Tab */}
        {activeTab === 'time' && (
          <div className="space-y-8">
            {!deepAnalytics?.time_analysis ? (
              <Card className="p-12 lg:p-16 text-center bg-card rounded-2xl border-2 border-border shadow-md">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6" aria-hidden="true">
                  <Timer className="text-blue-600 dark:text-blue-400" size={48} />
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-foreground dark:text-white mb-3">Time Analysis</h3>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto text-lg">
                  Run a deep scan to analyze your time management patterns
                </p>
                <Button onClick={loadDeepAnalytics} size="lg" className="gap-2" disabled={deepLoading}>
                  <Radar size={20} className={deepLoading ? 'animate-spin' : ''} aria-hidden="true" />
                  {deepLoading ? 'Analyzing...' : 'Run Deep Scan'}
                </Button>
              </Card>
            ) : (
              <>
                {/* Time Stats Overview */}
                <section aria-labelledby="time-stats-heading">
                  <h2 id="time-stats-heading" className="sr-only">Time Statistics Overview</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                    <Card className="p-6 bg-card rounded-2xl border-2 border-border shadow-md">
                      <Timer className="text-blue-500 mb-3" size={28} aria-hidden="true" />
                      <p className="text-muted-foreground font-medium mb-2">Avg Time/Question</p>
                      <p className="text-3xl font-bold text-foreground dark:text-white">
                        {deepAnalytics.time_analysis.average_time_seconds}s
                      </p>
                    </Card>
                    
                    <Card className="p-6 bg-card rounded-2xl border-2 border-border shadow-md">
                      <Hourglass className="text-purple-500 mb-3" size={28} aria-hidden="true" />
                      <p className="text-muted-foreground font-medium mb-2">Median Time</p>
                      <p className="text-3xl font-bold text-foreground dark:text-white">
                        {deepAnalytics.time_analysis.median_time_seconds}s
                      </p>
                    </Card>
                    
                    <Card className="p-6 bg-card rounded-2xl border-2 border-border shadow-md">
                      <Activity className="text-green-500 mb-3" size={28} aria-hidden="true" />
                      <p className="text-muted-foreground font-medium mb-2">Time Variability</p>
                      <p className="text-3xl font-bold text-foreground dark:text-white">
                        ±{deepAnalytics.time_analysis.time_variability}s
                      </p>
                    </Card>
                    
                    <Card className="p-6 bg-card rounded-2xl border-2 border-border shadow-md">
                      <Target className="text-orange-500 mb-3" size={28} aria-hidden="true" />
                      <p className="text-muted-foreground font-medium mb-2">Questions Timed</p>
                      <p className="text-3xl font-bold text-foreground dark:text-white">
                        {deepAnalytics.time_analysis.total_questions_timed}
                      </p>
                    </Card>
                  </div>
                </section>

                {/* Speed vs Accuracy */}
                <section aria-labelledby="speed-accuracy-heading">
                  <Card className="p-6 lg:p-8 bg-card rounded-2xl border-2 border-border shadow-md">
                    <h2 id="speed-accuracy-heading" className="text-xl lg:text-2xl font-bold text-foreground dark:text-white mb-8 flex items-center gap-3">
                      <Gauge className="text-blue-500" size={28} aria-hidden="true" />
                      Speed vs. Accuracy Analysis
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                      <div className={`text-center p-6 lg:p-8 rounded-2xl border-2 ${
                        deepAnalytics.time_analysis.rushing_penalty ? 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-700' : 'bg-muted/50 border-muted'
                      }`}>
                        <Zap className={`mx-auto mb-3 ${deepAnalytics.time_analysis.rushing_penalty ? 'text-red-500' : 'text-yellow-500'}`} size={32} aria-hidden="true" />
                        <p className="text-muted-foreground font-medium mb-2">Fast Answers</p>
                        <p className={`text-4xl font-bold mb-3 ${deepAnalytics.time_analysis.rushing_penalty ? 'text-red-600 dark:text-red-400' : 'text-foreground dark:text-white'}`}>
                          {deepAnalytics.time_analysis.speed_vs_accuracy?.fast || 0}%
                        </p>
                        {deepAnalytics.time_analysis.rushing_penalty && (
                          <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-base px-3 py-1">Rushing Detected</Badge>
                        )}
                      </div>
                      
                      <div className="text-center p-6 lg:p-8 bg-green-50 dark:bg-green-950/30 rounded-2xl border-2 border-green-300 dark:border-green-700">
                        <CheckCircle2 className="mx-auto mb-3 text-green-500" size={32} aria-hidden="true" />
                        <p className="text-muted-foreground font-medium mb-2">Normal Pace</p>
                        <p className="text-4xl font-bold text-green-600 dark:text-green-400 mb-3">
                          {deepAnalytics.time_analysis.speed_vs_accuracy?.normal || 0}%
                        </p>
                        <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-base px-3 py-1">Optimal</Badge>
                      </div>
                      
                      <div className={`text-center p-6 lg:p-8 rounded-2xl border-2 ${
                        deepAnalytics.time_analysis.overthinking_penalty ? 'bg-orange-50 dark:bg-orange-950/30 border-orange-300 dark:border-orange-700' : 'bg-muted/50 border-muted'
                      }`}>
                        <Coffee className={`mx-auto mb-3 ${deepAnalytics.time_analysis.overthinking_penalty ? 'text-orange-500' : 'text-blue-500'}`} size={32} aria-hidden="true" />
                        <p className="text-muted-foreground font-medium mb-2">Slow Answers</p>
                        <p className={`text-4xl font-bold mb-3 ${deepAnalytics.time_analysis.overthinking_penalty ? 'text-orange-600 dark:text-orange-400' : 'text-foreground dark:text-white'}`}>
                          {deepAnalytics.time_analysis.speed_vs_accuracy?.slow || 0}%
                        </p>
                        {deepAnalytics.time_analysis.overthinking_penalty && (
                          <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 text-base px-3 py-1">Overthinking</Badge>
                        )}
                      </div>
                    </div>

                    {deepAnalytics.time_analysis.optimal_time_range && (
                      <div className="p-6 bg-green-50 dark:bg-green-950/30 rounded-xl border-2 border-green-200 dark:border-green-700" role="note">
                        <p className="flex items-start gap-3 font-semibold text-green-700 dark:text-green-400 text-lg mb-2">
                          <Lightbulb size={24} className="shrink-0" aria-hidden="true" />
                          <span>Optimal Time: {deepAnalytics.time_analysis.optimal_time_range.avg_time}s per question</span>
                        </p>
                        <p className="text-muted-foreground ml-9">
                          Questions answered in this time range have {deepAnalytics.time_analysis.optimal_time_range.accuracy}% accuracy
                        </p>
                      </div>
                    )}
                  </Card>
                </section>

                {/* Time by Difficulty */}
                <section aria-labelledby="time-difficulty-heading">
                  <Card className="p-6 lg:p-8 bg-card rounded-2xl border-2 border-border shadow-md">
                    <h3 id="time-difficulty-heading" className="text-xl font-bold text-foreground dark:text-white mb-6">Time by Difficulty</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                      {Object.entries(deepAnalytics.time_analysis.time_by_difficulty || {}).map(([diff, time]) => (
                        <div key={diff} className="text-center p-6 bg-muted/50 rounded-xl border-2 border-muted">
                          <Badge className={`mb-3 text-base px-4 py-1 ${
                            diff === 'easy' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            diff === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {diff.charAt(0).toUpperCase() + diff.slice(1)}
                          </Badge>
                          <p className="text-3xl font-bold text-foreground dark:text-white mb-1">{time}s</p>
                          <p className="text-sm text-muted-foreground">average time</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                </section>

                {/* Time by Subject */}
                <section aria-labelledby="time-subject-heading">
                  <Card className="p-6 lg:p-8 bg-card rounded-2xl border-2 border-border shadow-md">
                    <h3 id="time-subject-heading" className="text-xl font-bold text-foreground dark:text-white mb-6">Time by Subject</h3>
                    <ul className="space-y-4">
                      {Object.entries(deepAnalytics.time_analysis.time_by_subject || {}).map(([subject, time]) => (
                        <li key={subject} className="flex items-center justify-between p-5 bg-muted/50 rounded-xl border border-muted">
                          <span className="font-medium text-foreground dark:text-white text-lg">{subject}</span>
                          <div className="flex items-center gap-4">
                            <div className="h-3 w-40 bg-muted rounded-full overflow-hidden" role="progressbar" aria-label={`${subject} time`} aria-valuenow={time} aria-valuemin="0" aria-valuemax="120">
                              <div 
                                className="h-full bg-blue-500 rounded-full transition-all"
                                style={{ width: `${Math.min((time / 120) * 100, 100)}%` }}
                              />
                            </div>
                            <span className="font-bold text-foreground dark:text-white w-16 text-right text-lg">{time}s</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </Card>
                </section>
              </>
            )}
          </div>
        )}

        {/* Behavior Tab */}
        {activeTab === 'behavior' && (
          <div className="space-y-6">
            {!deepAnalytics?.behavioral_patterns ? (
              <Card className="p-12 text-center bg-card rounded-2xl border border-border">
                <Activity className="mx-auto mb-4 text-muted-foreground" size={48} />
                <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">Behavioral Analysis</h3>
                <p className="text-muted-foreground mb-6">Run a deep scan to analyze your test-taking behavior</p>
                <Button onClick={loadDeepAnalytics} className="gap-2" disabled={deepLoading}>
                  <Radar size={18} className={deepLoading ? 'animate-spin' : ''} />
                  {deepLoading ? 'Analyzing...' : 'Run Deep Scan'}
                </Button>
              </Card>
            ) : (
              <>
                {/* First Instinct Analysis */}
                <Card className="p-6 bg-card rounded-2xl border border-border">
                  <h3 className="text-lg font-bold text-foreground dark:text-white mb-6 flex items-center gap-2">
                    <Eye className="text-purple-500" size={24} />
                    First Instinct Analysis
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 rounded-2xl">
                      <p className="text-muted-foreground mb-2">First Answer Accuracy</p>
                      <p className="text-5xl font-bold text-purple-600 dark:text-purple-400">
                        {deepAnalytics.behavioral_patterns.first_instinct_accuracy}%
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">When you don't change your answer</p>
                    </div>
                    
                    <div className="text-center p-6 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/30 rounded-2xl">
                      <p className="text-muted-foreground mb-2">Changed Answer Accuracy</p>
                      <p className="text-5xl font-bold text-orange-600 dark:text-orange-400">
                        {deepAnalytics.behavioral_patterns.change_success_rate}%
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">When you change your answer</p>
                    </div>
                  </div>

                  <div className={`p-4 rounded-xl border ${
                    deepAnalytics.behavioral_patterns.trust_first_instinct 
                      ? 'bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800' 
                      : 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800'
                  }`}>
                    <p className="flex items-center gap-2 font-semibold text-foreground dark:text-white">
                      <Lightbulb className="text-yellow-500" size={20} />
                      {deepAnalytics.behavioral_patterns.first_instinct_recommendation}
                    </p>
                  </div>
                </Card>

                {/* Behavior Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="p-5 bg-card rounded-2xl border border-border">
                    <RotateCcw className="text-orange-500 mb-2" size={24} />
                    <p className="text-muted-foreground text-sm">Answer Change Rate</p>
                    <p className="text-2xl font-bold text-foreground dark:text-white">
                      {deepAnalytics.behavioral_patterns.answer_change_rate}%
                    </p>
                  </Card>
                  
                  <Card className="p-5 bg-card rounded-2xl border border-border">
                    <SkipForward className="text-blue-500 mb-2" size={24} />
                    <p className="text-muted-foreground text-sm">Skip Rate</p>
                    <p className="text-2xl font-bold text-foreground dark:text-white">
                      {deepAnalytics.behavioral_patterns.skip_rate}%
                    </p>
                  </Card>
                  
                  <Card className="p-5 bg-card rounded-2xl border border-border">
                    <PenTool className="text-purple-500 mb-2" size={24} />
                    <p className="text-muted-foreground text-sm">Review Usage</p>
                    <p className="text-2xl font-bold text-foreground dark:text-white">
                      {deepAnalytics.behavioral_patterns.review_usage_rate}%
                    </p>
                  </Card>
                  
                  <Card className="p-5 bg-card rounded-2xl border border-border">
                    <GitBranch className="text-green-500 mb-2" size={24} />
                    <p className="text-muted-foreground text-sm">Multi-Visit Rate</p>
                    <p className="text-2xl font-bold text-foreground dark:text-white">
                      {deepAnalytics.behavioral_patterns.multi_visit_rate}%
                    </p>
                  </Card>
                </div>

                {/* Skip Strategy */}
                <Card className="p-6 bg-card rounded-2xl border border-border">
                  <h3 className="text-lg font-bold text-foreground dark:text-white mb-4">Skip & Return Strategy</h3>
                  <div className="flex items-center gap-4">
                    <div className={`flex-1 p-4 rounded-xl ${
                      deepAnalytics.behavioral_patterns.strategic_skipping 
                        ? 'bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800'
                        : 'bg-muted/50'
                    }`}>
                      <p className="text-muted-foreground text-sm mb-1">Skip Accuracy</p>
                      <p className="text-3xl font-bold text-foreground dark:text-white">
                        {deepAnalytics.behavioral_patterns.skip_accuracy}%
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Accuracy of questions you initially skipped
                      </p>
                    </div>
                    <ArrowUp className={`${
                      deepAnalytics.behavioral_patterns.strategic_skipping ? 'text-green-500' : 'text-muted-foreground'
                    }`} size={24} />
                    <p className="text-sm text-muted-foreground">
                      {deepAnalytics.behavioral_patterns.strategic_skipping 
                        ? 'Good strategy! Skipping helps you think better' 
                        : 'Skipping may not be helping you'}
                    </p>
                  </div>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Predictions Tab */}
        {activeTab === 'predictions' && (
          <div className="space-y-6">
            {!deepAnalytics?.predictions ? (
              <Card className="p-12 text-center bg-card rounded-2xl border border-border">
                <TrendingUp className="mx-auto mb-4 text-muted-foreground" size={48} />
                <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">Predictive Analytics</h3>
                <p className="text-muted-foreground mb-6">Run a deep scan to see your performance predictions</p>
                <Button onClick={loadDeepAnalytics} className="gap-2" disabled={deepLoading}>
                  <Radar size={18} className={deepLoading ? 'animate-spin' : ''} />
                  {deepLoading ? 'Analyzing...' : 'Run Deep Scan'}
                </Button>
              </Card>
            ) : (
              <>
                {/* Trend Overview */}
                <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 rounded-2xl border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-foreground dark:text-white flex items-center gap-2">
                      <LineChart className="text-blue-500" size={24} />
                      Performance Trend
                    </h3>
                    <Badge className={`${
                      deepAnalytics.predictions.trend_direction === 'improving' ? 'bg-green-100 text-green-700' :
                      deepAnalytics.predictions.trend_direction === 'declining' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {deepAnalytics.predictions.trend_direction === 'improving' ? '📈 Improving' :
                       deepAnalytics.predictions.trend_direction === 'declining' ? '📉 Declining' : '➡️ Stable'}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-6 text-center">
                    <div>
                      <p className="text-muted-foreground text-sm mb-2">Current</p>
                      <p className="text-4xl font-bold text-foreground dark:text-white">
                        {deepAnalytics.predictions.current_accuracy}%
                      </p>
                    </div>
                    <div className="flex items-center justify-center">
                      {deepAnalytics.predictions.trend_direction === 'improving' ? (
                        <ArrowUp className="text-green-500" size={48} />
                      ) : deepAnalytics.predictions.trend_direction === 'declining' ? (
                        <ArrowDown className="text-red-500" size={48} />
                      ) : (
                        <div className="w-12 h-0.5 bg-yellow-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-muted-foreground text-sm mb-2">Predicted (5 tests)</p>
                      <p className={`text-4xl font-bold ${
                        deepAnalytics.predictions.predicted_in_5_tests > deepAnalytics.predictions.current_accuracy 
                          ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {deepAnalytics.predictions.predicted_in_5_tests}%
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Subject Predictions */}
                <Card className="p-6 bg-card rounded-2xl border border-border">
                  <h3 className="text-lg font-bold text-foreground dark:text-white mb-4">Subject Trends</h3>
                  <div className="space-y-4">
                    {Object.entries(deepAnalytics.predictions.subject_predictions || {}).map(([subject, data]) => (
                      <div key={subject} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            data.trend === 'improving' ? 'bg-green-100 dark:bg-green-900/30' :
                            data.trend === 'declining' ? 'bg-red-100 dark:bg-red-900/30' :
                            'bg-yellow-100 dark:bg-yellow-900/30'
                          }`}>
                            {data.trend === 'improving' ? <TrendingUp className="text-green-500" size={20} /> :
                             data.trend === 'declining' ? <TrendingDown className="text-red-500" size={20} /> :
                             <LineChart className="text-yellow-500" size={20} />}
                          </div>
                          <div>
                            <p className="font-medium text-foreground dark:text-white">{subject}</p>
                            <p className="text-sm text-muted-foreground capitalize">{data.trend}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${getAccuracyColor(data.current)}`}>
                            {data.current}%
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Mastery Estimates */}
                {Object.keys(deepAnalytics.predictions.mastery_estimates || {}).length > 0 && (
                  <Card className="p-6 bg-card rounded-2xl border border-border">
                    <h3 className="text-lg font-bold text-foreground dark:text-white mb-4 flex items-center gap-2">
                      <Target className="text-green-500" size={24} />
                      Time to 80% Mastery
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(deepAnalytics.predictions.mastery_estimates).map(([subject, days]) => (
                        <div key={subject} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                          <span className="font-medium text-foreground dark:text-white">{subject}</span>
                          <Badge className="bg-blue-100 text-blue-700">
                            ~{days} days
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </>
            )}
          </div>
        )}

        {/* Benchmarks Tab */}
        {activeTab === 'benchmarks' && (
          <div className="space-y-6">
            {!deepAnalytics?.benchmarking ? (
              <Card className="p-12 text-center bg-card rounded-2xl border border-border">
                <Users className="mx-auto mb-4 text-muted-foreground" size={48} />
                <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">Competitive Ranking</h3>
                <p className="text-muted-foreground mb-6">Run a deep scan to see how you compare to others</p>
                <Button onClick={loadDeepAnalytics} className="gap-2" disabled={deepLoading}>
                  <Radar size={18} className={deepLoading ? 'animate-spin' : ''} />
                  {deepLoading ? 'Analyzing...' : 'Run Deep Scan'}
                </Button>
              </Card>
            ) : (
              <>
                {/* Rank Card */}
                <Card className="p-8 bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 text-white rounded-2xl">
                  <div className="text-center">
                    <Trophy className="mx-auto mb-4" size={64} />
                    <p className="text-yellow-100 text-lg mb-2">Your Rank</p>
                    <p className="text-6xl font-bold mb-2">
                      #{deepAnalytics.benchmarking.rank_position}
                    </p>
                    <p className="text-yellow-100">
                      out of {deepAnalytics.benchmarking.total_users_compared} users
                    </p>
                    
                    <div className="mt-6 flex justify-center gap-4">
                      {deepAnalytics.benchmarking.top_10_percent && (
                        <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                          Top 10%
                        </Badge>
                      )}
                      {deepAnalytics.benchmarking.top_25_percent && !deepAnalytics.benchmarking.top_10_percent && (
                        <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                          🥈 Top 25%
                        </Badge>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Percentile */}
                <Card className="p-6 bg-card rounded-2xl border border-border">
                  <h3 className="text-lg font-bold text-foreground dark:text-white mb-4">Percentile Ranking</h3>
                  <div className="relative h-8 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full mb-4">
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-white border-4 border-blue-500 rounded-full shadow-lg transition-all"
                      style={{ left: `calc(${deepAnalytics.benchmarking.overall_percentile}% - 12px)` }}
                    />
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>0%</span>
                    <span className="font-bold text-foreground dark:text-white">
                      You: {deepAnalytics.benchmarking.overall_percentile}th percentile
                    </span>
                    <span>100%</span>
                  </div>
                </Card>

                {/* Subject Benchmarks */}
                <Card className="p-6 bg-card rounded-2xl border border-border">
                  <h3 className="text-lg font-bold text-foreground dark:text-white mb-4">Subject Comparison vs Platform Average</h3>
                  <div className="space-y-4">
                    {Object.entries(deepAnalytics.benchmarking.subject_benchmarks || {}).map(([subject, data]) => (
                      <div key={subject} className="p-4 bg-muted/50 rounded-xl">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium text-foreground dark:text-white">{subject}</span>
                          <Badge className={data.above_average ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {data.difference > 0 ? '+' : ''}{data.difference}%
                          </Badge>
                        </div>
                        <div className="relative h-6 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="absolute h-full bg-blue-500/30 rounded-full"
                            style={{ width: `${data.platform_average}%` }}
                          />
                          <div 
                            className={`absolute h-full rounded-full ${data.above_average ? 'bg-green-500' : 'bg-red-500'}`}
                            style={{ width: `${data.your_accuracy}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>You: {data.your_accuracy}%</span>
                          <span>Platform Avg: {data.platform_average}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Mistakes Tab */}
        {activeTab === 'mistakes' && (
          <div className="space-y-6">
            {!deepAnalytics?.mistake_patterns ? (
              <Card className="p-12 text-center bg-card rounded-2xl border border-border">
                <XCircle className="mx-auto mb-4 text-muted-foreground" size={48} />
                <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">Mistake Analysis</h3>
                <p className="text-muted-foreground mb-6">Run a deep scan to analyze your mistake patterns</p>
                <Button onClick={loadDeepAnalytics} className="gap-2" disabled={deepLoading}>
                  <Radar size={18} className={deepLoading ? 'animate-spin' : ''} />
                  {deepLoading ? 'Analyzing...' : 'Run Deep Scan'}
                </Button>
              </Card>
            ) : (
              <>
                {/* Mistake Types */}
                <Card className="p-6 bg-card rounded-2xl border border-border">
                  <h3 className="text-lg font-bold text-foreground dark:text-white mb-6 flex items-center gap-2">
                    <Layers className="text-red-500" size={24} />
                    Mistake Classification
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-6 bg-yellow-50 dark:bg-yellow-950/30 rounded-2xl border border-yellow-200 dark:border-yellow-800">
                      <Zap className="mx-auto text-yellow-500 mb-2" size={32} />
                      <p className="text-muted-foreground text-sm">Careless Mistakes</p>
                      <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                        {deepAnalytics.mistake_patterns.careless_mistakes}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        ({deepAnalytics.mistake_patterns.careless_rate}% of errors)
                      </p>
                    </div>
                    
                    <div className="text-center p-6 bg-red-50 dark:bg-red-950/30 rounded-2xl border border-red-200 dark:border-red-800">
                      <AlertCircle className="mx-auto text-red-500 mb-2" size={32} />
                      <p className="text-muted-foreground text-sm">Knowledge Gaps</p>
                      <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                        {deepAnalytics.mistake_patterns.knowledge_gap_mistakes}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        ({deepAnalytics.mistake_patterns.knowledge_gap_rate}% of errors)
                      </p>
                    </div>
                    
                    <div className="text-center p-6 bg-orange-50 dark:bg-orange-950/30 rounded-2xl border border-orange-200 dark:border-orange-800">
                      <RotateCcw className="mx-auto text-orange-500 mb-2" size={32} />
                      <p className="text-muted-foreground text-sm">Changed to Wrong</p>
                      <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                        {deepAnalytics.mistake_patterns.changed_to_wrong}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        ({deepAnalytics.mistake_patterns.changed_to_wrong_rate}% of errors)
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
                    <p className="flex items-start gap-2">
                      <Lightbulb className="text-blue-500 shrink-0 mt-1" size={18} />
                      <span className="text-foreground dark:text-white">
                        {deepAnalytics.mistake_patterns.recommendation}
                      </span>
                    </p>
                  </div>
                </Card>

                {/* Mistakes by Difficulty */}
                <Card className="p-6 bg-card rounded-2xl border border-border">
                  <h3 className="text-lg font-bold text-foreground dark:text-white mb-4">Mistakes by Difficulty</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(deepAnalytics.mistake_patterns.mistakes_by_difficulty || {}).map(([diff, count]) => (
                      <div key={diff} className="text-center p-4 bg-muted/50 rounded-xl">
                        <Badge className={`mb-2 ${
                          diff === 'easy' ? 'bg-green-100 text-green-700' :
                          diff === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {diff}
                        </Badge>
                        <p className="text-2xl font-bold text-foreground dark:text-white">{count}</p>
                        <p className="text-xs text-muted-foreground">mistakes</p>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Top Mistake Chapters */}
                {deepAnalytics.mistake_patterns.top_mistake_chapters?.length > 0 && (
                  <Card className="p-6 bg-red-50 dark:bg-red-950/20 rounded-2xl border border-red-200 dark:border-red-800">
                    <h3 className="text-lg font-bold text-foreground dark:text-white mb-4 flex items-center gap-2">
                      <AlertCircle className="text-red-500" size={24} />
                      Most Problematic Topics
                    </h3>
                    <div className="space-y-3">
                      {deepAnalytics.mistake_patterns.top_mistake_chapters.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center text-red-600 font-bold">
                              {i + 1}
                            </div>
                            <span className="font-medium text-foreground dark:text-white">{item.chapter}</span>
                          </div>
                          <Badge className="bg-red-100 text-red-700">
                            {item.count} mistakes
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Fatigue-Related Mistakes */}
                <Card className="p-6 bg-card rounded-2xl border border-border">
                  <h3 className="text-lg font-bold text-foreground dark:text-white mb-4 flex items-center gap-2">
                    <Battery className="text-orange-500" size={24} />
                    Fatigue-Related Mistakes
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="relative w-24 h-24">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle cx="48" cy="48" r="40" fill="none" stroke="currentColor" strokeWidth="8" className="text-muted" />
                        <circle 
                          cx="48" cy="48" r="40" fill="none" stroke="currentColor" strokeWidth="8" 
                          strokeDasharray={`${deepAnalytics.mistake_patterns.fatigue_related_rate * 2.51} 251`}
                          className="text-orange-500"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xl font-bold text-foreground dark:text-white">
                          {deepAnalytics.mistake_patterns.fatigue_related_rate}%
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground dark:text-white">
                        {deepAnalytics.mistake_patterns.late_question_mistakes} mistakes in later questions
                      </p>
                      <p className="text-sm text-muted-foreground">
                        These are questions past position 20, where fatigue typically sets in
                      </p>
                    </div>
                  </div>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Exam Readiness Tab */}
        {activeTab === 'readiness' && (
          <div className="space-y-6">
            {!deepAnalytics?.readiness_score ? (
              <Card className="p-12 text-center bg-card rounded-2xl border border-border">
                <Shield className="mx-auto mb-4 text-muted-foreground" size={48} />
                <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">Exam Readiness Score</h3>
                <p className="text-muted-foreground mb-6">Run a deep scan to calculate your exam readiness</p>
                <Button onClick={loadDeepAnalytics} className="gap-2" disabled={deepLoading}>
                  <Radar size={18} className={deepLoading ? 'animate-spin' : ''} />
                  {deepLoading ? 'Analyzing...' : 'Calculate Readiness'}
                </Button>
              </Card>
            ) : (
              <>
                {/* Overall Readiness Score */}
                <Card className="p-8 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white rounded-2xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-indigo-100 mb-2">Exam Readiness Score</p>
                      <p className="text-7xl font-bold mb-2">{deepAnalytics.readiness_score.overall_score}</p>
                      <Badge className="bg-white/20 text-white text-lg px-4 py-1">
                        {deepAnalytics.readiness_score.level}
                      </Badge>
                    </div>
                    <div className="text-right">
                      <Shield size={100} className="opacity-30" />
                    </div>
                  </div>
                  <p className="mt-6 text-indigo-100 text-lg">
                    {deepAnalytics.readiness_score.message}
                  </p>
                </Card>

                {/* Breakdown */}
                <Card className="p-6 bg-card rounded-2xl border border-border">
                  <h3 className="text-lg font-bold text-foreground dark:text-white mb-6">Readiness Breakdown</h3>
                  <div className="space-y-4">
                    {Object.entries(deepAnalytics.readiness_score.breakdown || {}).map(([key, value]) => (
                      <div key={key}>
                        <div className="flex justify-between mb-2">
                          <span className="capitalize text-foreground dark:text-white font-medium">
                            {key.replace('_', ' ')}
                          </span>
                          <span className={`font-bold ${value >= 70 ? 'text-green-500' : value >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>
                            {value}%
                          </span>
                        </div>
                        <div className="h-3 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                              value >= 70 ? 'bg-gradient-to-r from-green-400 to-green-600' : 
                              value >= 50 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' : 
                              'bg-gradient-to-r from-red-400 to-red-600'
                            }`}
                            style={{ width: `${value}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Card className="p-6 bg-card rounded-2xl border border-border text-center">
                    <BookOpen className="mx-auto text-blue-500 mb-2" size={32} />
                    <p className="text-3xl font-bold text-foreground dark:text-white">
                      {deepAnalytics.readiness_score.total_questions_practiced}
                    </p>
                    <p className="text-sm text-muted-foreground">Questions Practiced</p>
                  </Card>
                  <Card className="p-6 bg-card rounded-2xl border border-border text-center">
                    <Layers className="mx-auto text-purple-500 mb-2" size={32} />
                    <p className="text-3xl font-bold text-foreground dark:text-white">
                      {deepAnalytics.readiness_score.subjects_covered?.length || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Subjects Covered</p>
                  </Card>
                  <Card className="p-6 bg-card rounded-2xl border border-border text-center md:col-span-1 col-span-2">
                    <Star className="mx-auto text-yellow-500 mb-2" size={32} />
                    <p className="text-sm text-muted-foreground">Target Score</p>
                    <p className="text-2xl font-bold text-foreground dark:text-white">
                      {deepAnalytics.readiness_score.overall_score >= 70 ? 'On Track' : 'Needs Focus'}
                    </p>
                  </Card>
                </div>
              </>
            )}
          </div>
        )}

        {/* Peak Hours Tab */}
        {activeTab === 'peak' && (
          <div className="space-y-6">
            {!deepAnalytics?.peak_performance ? (
              <Card className="p-12 text-center bg-card rounded-2xl border border-border">
                <Sun className="mx-auto mb-4 text-muted-foreground" size={48} />
                <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">Peak Performance Analysis</h3>
                <p className="text-muted-foreground mb-6">Run a deep scan to find your optimal study times</p>
                <Button onClick={loadDeepAnalytics} className="gap-2" disabled={deepLoading}>
                  <Radar size={18} className={deepLoading ? 'animate-spin' : ''} />
                  {deepLoading ? 'Analyzing...' : 'Find Peak Hours'}
                </Button>
              </Card>
            ) : (
              <>
                {/* Best Time Hero */}
                <Card className="p-8 bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 text-white rounded-2xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100 mb-2">Your Peak Performance Time</p>
                      <p className="text-5xl font-bold mb-2">{deepAnalytics.peak_performance.best_period}</p>
                      {deepAnalytics.peak_performance.best_hour !== null && (
                        <p className="text-amber-100">
                          Best accuracy at {deepAnalytics.peak_performance.best_hour}:00 - {deepAnalytics.peak_performance.best_hour_accuracy}%
                        </p>
                      )}
                    </div>
                    <Sun size={80} className="opacity-30" />
                  </div>
                </Card>

                {/* Time Period Breakdown */}
                <Card className="p-6 bg-card rounded-2xl border border-border">
                  <h3 className="text-lg font-bold text-foreground dark:text-white mb-6">Performance by Time of Day</h3>
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { key: 'morning', icon: Sunrise, label: 'Morning', color: 'orange' },
                      { key: 'afternoon', icon: Sun, label: 'Afternoon', color: 'yellow' },
                      { key: 'evening', icon: Sunset, label: 'Evening', color: 'purple' },
                      { key: 'night', icon: Moon, label: 'Night', color: 'blue' }
                    ].map(({ key, icon: Icon, label, color }) => (
                      <div key={key} className={`text-center p-4 rounded-xl bg-${color}-50 dark:bg-${color}-900/20`}>
                        <Icon className={`mx-auto mb-2 text-${color}-500`} size={28} />
                        <p className="text-sm text-muted-foreground">{label}</p>
                        <p className="text-2xl font-bold text-foreground dark:text-white">
                          {deepAnalytics.peak_performance.period_breakdown?.[key] || '-'}%
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Day Breakdown */}
                {deepAnalytics.peak_performance.day_breakdown && Object.keys(deepAnalytics.peak_performance.day_breakdown).length > 0 && (
                  <Card className="p-6 bg-card rounded-2xl border border-border">
                    <h3 className="text-lg font-bold text-foreground dark:text-white mb-6">Performance by Day</h3>
                    <div className="grid grid-cols-7 gap-2">
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                        const accuracy = deepAnalytics.peak_performance.day_breakdown?.[day];
                        return (
                          <div key={day} className={`text-center p-3 rounded-xl ${
                            accuracy >= 70 ? 'bg-green-100 dark:bg-green-900/30' :
                            accuracy >= 50 ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                            accuracy ? 'bg-red-100 dark:bg-red-900/30' :
                            'bg-muted/50'
                          }`}>
                            <p className="text-xs text-muted-foreground">{day.slice(0, 3)}</p>
                            <p className="text-lg font-bold text-foreground dark:text-white">
                              {accuracy ? `${accuracy}%` : '-'}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}

                <Card className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
                  <p className="flex items-start gap-2">
                    <Lightbulb className="text-blue-500 shrink-0 mt-1" size={18} />
                    <span className="text-foreground dark:text-white">
                      {deepAnalytics.peak_performance.recommendation}
                    </span>
                  </p>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Topic Map Tab */}
        {activeTab === 'topics' && (
          <div className="space-y-6">
            {!deepAnalytics?.topic_strength ? (
              <Card className="p-12 text-center bg-card rounded-2xl border border-border">
                <Map className="mx-auto mb-4 text-muted-foreground" size={48} />
                <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">Topic Strength Map</h3>
                <p className="text-muted-foreground mb-6">Run a deep scan to see your strengths and weaknesses by topic</p>
                <Button onClick={loadDeepAnalytics} className="gap-2" disabled={deepLoading}>
                  <Radar size={18} className={deepLoading ? 'animate-spin' : ''} />
                  {deepLoading ? 'Analyzing...' : 'Analyze Topics'}
                </Button>
              </Card>
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="p-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 rounded-2xl text-center">
                    <CheckCircle2 className="mx-auto text-green-500 mb-2" size={32} />
                    <p className="text-3xl font-bold text-green-600">{deepAnalytics.topic_strength.strong_count}</p>
                    <p className="text-sm text-muted-foreground">Strong Topics</p>
                  </Card>
                  <Card className="p-6 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 rounded-2xl text-center">
                    <AlertCircle className="mx-auto text-yellow-500 mb-2" size={32} />
                    <p className="text-3xl font-bold text-yellow-600">{deepAnalytics.topic_strength.total_topics - deepAnalytics.topic_strength.strong_count - deepAnalytics.topic_strength.weak_count}</p>
                    <p className="text-sm text-muted-foreground">Moderate Topics</p>
                  </Card>
                  <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 rounded-2xl text-center">
                    <XCircle className="mx-auto text-red-500 mb-2" size={32} />
                    <p className="text-3xl font-bold text-red-600">{deepAnalytics.topic_strength.weak_count}</p>
                    <p className="text-sm text-muted-foreground">Weak Topics</p>
                  </Card>
                </div>

                {/* Priority Focus */}
                {deepAnalytics.topic_strength.priority_focus?.length > 0 && (
                  <Card className="p-6 bg-red-50 dark:bg-red-950/30 rounded-2xl border border-red-200 dark:border-red-800">
                    <h3 className="text-lg font-bold text-red-600 dark:text-red-400 mb-4 flex items-center gap-2">
                      <AlertCircle size={24} />
                      Priority Focus Areas
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {deepAnalytics.topic_strength.priority_focus.map((topic, i) => (
                        <Badge key={i} className="bg-red-100 text-red-700 text-sm px-3 py-1">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </Card>
                )}

                {/* All Topics */}
                <Card className="p-6 bg-card rounded-2xl border border-border">
                  <h3 className="text-lg font-bold text-foreground dark:text-white mb-6">All Topics</h3>
                  <div className="space-y-3">
                    {deepAnalytics.topic_strength.all_topics?.map((topic, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${
                            topic.strength === 'strong' ? 'bg-green-500' :
                            topic.strength === 'moderate' ? 'bg-yellow-500' :
                            topic.strength === 'weak' ? 'bg-orange-500' :
                            'bg-red-500'
                          }`} />
                          <span className="font-medium text-foreground dark:text-white">{topic.topic}</span>
                          {topic.trend === 'improving' && <ArrowUp className="text-green-500" size={16} />}
                          {topic.trend === 'declining' && <ArrowDown className="text-red-500" size={16} />}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-muted-foreground">{topic.total_questions} Q</span>
                          <Badge className={`${
                            topic.accuracy >= 70 ? 'bg-green-100 text-green-700' :
                            topic.accuracy >= 50 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {topic.accuracy}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Study Habits Tab */}
        {activeTab === 'study' && (
          <div className="space-y-6">
            {!deepAnalytics?.study_patterns ? (
              <Card className="p-12 text-center bg-card rounded-2xl border border-border">
                <CalendarCheck className="mx-auto mb-4 text-muted-foreground" size={48} />
                <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">Study Patterns</h3>
                <p className="text-muted-foreground mb-6">Run a deep scan to analyze your study habits</p>
                <Button onClick={loadDeepAnalytics} className="gap-2" disabled={deepLoading}>
                  <Radar size={18} className={deepLoading ? 'animate-spin' : ''} />
                  {deepLoading ? 'Analyzing...' : 'Analyze Habits'}
                </Button>
              </Card>
            ) : (
              <>
                {/* Streak Card */}
                <Card className="p-8 bg-gradient-to-br from-orange-400 via-red-500 to-pink-500 text-white rounded-2xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 mb-2">Current Streak</p>
                      <div className="flex items-center gap-3">
                        <Flame size={48} />
                        <p className="text-6xl font-bold">{deepAnalytics.study_patterns.current_streak}</p>
                        <p className="text-2xl">days</p>
                      </div>
                      <p className="mt-4 text-orange-100">
                        Personal best: {deepAnalytics.study_patterns.max_streak} days
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-orange-100">Consistency</p>
                      <p className="text-5xl font-bold">{deepAnalytics.study_patterns.consistency_score}%</p>
                    </div>
                  </div>
                </Card>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="p-6 bg-card rounded-2xl border border-border text-center">
                    <Calendar className="mx-auto text-blue-500 mb-2" size={32} />
                    <p className="text-3xl font-bold text-foreground dark:text-white">
                      {deepAnalytics.study_patterns.total_active_days}
                    </p>
                    <p className="text-sm text-muted-foreground">Active Days</p>
                  </Card>
                  <Card className="p-6 bg-card rounded-2xl border border-border text-center">
                    <BookOpen className="mx-auto text-purple-500 mb-2" size={32} />
                    <p className="text-3xl font-bold text-foreground dark:text-white">
                      {deepAnalytics.study_patterns.avg_questions_per_day}
                    </p>
                    <p className="text-sm text-muted-foreground">Q/Day Average</p>
                  </Card>
                  <Card className="p-6 bg-card rounded-2xl border border-border text-center">
                    <Trophy className="mx-auto text-yellow-500 mb-2" size={32} />
                    <p className="text-3xl font-bold text-foreground dark:text-white">
                      {deepAnalytics.study_patterns.peak_study_day}
                    </p>
                    <p className="text-sm text-muted-foreground">Peak Day</p>
                  </Card>
                  <Card className="p-6 bg-card rounded-2xl border border-border text-center">
                    <BarChart3 className="mx-auto text-green-500 mb-2" size={32} />
                    <p className="text-3xl font-bold text-foreground dark:text-white">
                      {deepAnalytics.study_patterns.weeks_active}
                    </p>
                    <p className="text-sm text-muted-foreground">Weeks Active</p>
                  </Card>
                </div>

                <Card className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
                  <p className="flex items-start gap-2">
                    <Lightbulb className="text-blue-500 shrink-0 mt-1" size={18} />
                    <span className="text-foreground dark:text-white">
                      {deepAnalytics.study_patterns.recommendation}
                    </span>
                  </p>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Test History Tab */}
        {activeTab === 'test-history' && (
          <div className="space-y-6">
            {loadingTests ? (
              <LoadingSpinner text="Loading test history..." />
            ) : testHistory.length === 0 ? (
              <Card className="p-12 text-center bg-card rounded-2xl border border-border">
                <Trophy className="mx-auto mb-4 text-muted-foreground" size={48} />
                <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">No Tests Completed Yet</h3>
                <p className="text-muted-foreground mb-6">Take your first test to see detailed results and analytics here.</p>
                <Button onClick={() => navigate('/exams')} className="gap-2">
                  <Target size={18} />
                  Take a Test
                </Button>
              </Card>
            ) : (
              <div>
                {/* Test List View */}
                {!selectedTest ? (
                  <div className="space-y-4">
                    <div className="mb-6">
                      <h2 className="text-2xl font-bold text-foreground mb-2">Your Test History</h2>
                      <p className="text-muted-foreground">Click on any test to see detailed breakdown</p>
                    </div>
                    {testHistory.map(test => (
                      <Card 
                        key={test.id}
                        className="p-6 bg-card rounded-xl border border-border hover:border-primary/50 transition-all cursor-pointer hover:shadow-lg"
                        onClick={() => loadTestDetails(test.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-bold text-foreground">{test.exam_name}</h3>
                              <Badge className={test.test_type === 'revision' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                                {test.test_type === 'revision' ? 'Revision' : test.test_type === 'practice' ? 'Practice' : 'Test'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-6 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Calendar size={14} />
                                {new Date(test.completed_at || test.created_at).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                })}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock size={14} />
                                {new Date(test.completed_at || test.created_at).toLocaleTimeString('en-US', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-center">
                              {/* Calculate marks based on scoring system */}
                              {(() => {
                                const totalMarks = (test.score * 4) + ((test.total - test.score) * -1);
                                const maxMarks = test.total * 4;
                                const percentage = (totalMarks / maxMarks * 100);
                                return (
                                  <>
                                    <div className={`text-3xl font-bold ${
                                      percentage >= 80 ? 'text-green-500' :
                                      percentage >= 60 ? 'text-yellow-500' : 'text-red-500'
                                    }`}>
                                      {totalMarks}/{maxMarks}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {Math.round(percentage)}% • {test.score} correct
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                            <ArrowRight className="text-muted-foreground" size={20} />
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  /* Test Detail View */
                  <div className="space-y-6">
                    <Button 
                      onClick={() => { setSelectedTest(null); setTestDetails(null); }}
                      variant="outline"
                      className="gap-2"
                    >
                      <ArrowLeft size={16} />
                      Back to Test History
                    </Button>

                    {loadingTestDetails ? (
                      <LoadingSpinner text="Loading test details..." />
                    ) : testDetails && (
                      <div className="space-y-6">
                        {/* Test Header */}
                        <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl border-2">
                          <div className="flex items-start justify-between mb-6">
                            <div>
                              <h2 className="text-2xl font-bold text-foreground mb-2">{testDetails.exam_name}</h2>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <Calendar size={14} />
                                  {new Date(testDetails.completed_at || testDetails.created_at).toLocaleDateString()}
                                </div>
                                {testDetails.time_taken && (
                                  <div className="flex items-center gap-1">
                                    <Clock size={14} />
                                    {Math.floor(testDetails.time_taken / 60)} min {testDetails.time_taken % 60} sec
                                  </div>
                                )}
                              </div>
                            </div>
                            <Badge className={
                              testDetails.stats.accuracy >= 80 ? 'bg-green-100 text-green-700 text-lg px-4 py-2' :
                              testDetails.stats.accuracy >= 60 ? 'bg-yellow-100 text-yellow-700 text-lg px-4 py-2' : 
                              'bg-red-100 text-red-700 text-lg px-4 py-2'
                            }>
                              {testDetails.stats.accuracy}% Accuracy
                            </Badge>
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                              <div className="text-2xl font-bold text-green-600">{testDetails.stats.correct}</div>
                              <div className="text-xs text-muted-foreground">Correct</div>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                              <div className="text-2xl font-bold text-red-600">{testDetails.stats.wrong}</div>
                              <div className="text-xs text-muted-foreground">Wrong</div>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                              <div className="text-2xl font-bold text-gray-600">{testDetails.stats.skipped}</div>
                              <div className="text-xs text-muted-foreground">Skipped</div>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                              <div className="text-2xl font-bold text-blue-600">{testDetails.stats.attempted}</div>
                              <div className="text-xs text-muted-foreground">Attempted</div>
                            </div>
                          </div>
                        </Card>

                        {/* Test Configuration Card */}
                        <Card className="p-6 rounded-xl border">
                          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                            <Settings size={18} className="text-primary" />
                            Test Configuration
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                                <Target size={12} />
                                <span>Questions</span>
                              </div>
                              <div className="text-xl font-bold text-foreground">{testDetails.questions.length}</div>
                            </div>
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                                <Clock size={12} />
                                <span>Time Limit</span>
                              </div>
                              <div className="text-xl font-bold text-foreground">
                                {testDetails.time_limit_minutes ? `${testDetails.time_limit_minutes} min` : 'Unlimited'}
                              </div>
                            </div>
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                                <Trophy size={12} />
                                <span>Test Type</span>
                              </div>
                              <div className="text-sm font-bold text-foreground capitalize">{testDetails.test_type || 'Standard'}</div>
                            </div>
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                                <BookOpen size={12} />
                                <span>Subjects</span>
                              </div>
                              <div className="text-xl font-bold text-foreground">
                                {Object.keys(testDetails.stats.subject_breakdown || {}).length}
                              </div>
                            </div>
                          </div>
                          
                          {/* Subjects included */}
                          {Object.keys(testDetails.stats.subject_breakdown || {}).length > 0 && (
                            <div className="mt-4 pt-4 border-t border-border">
                              <div className="text-xs font-semibold text-muted-foreground mb-2">SUBJECTS INCLUDED</div>
                              <div className="flex flex-wrap gap-2">
                                {Object.keys(testDetails.stats.subject_breakdown || {}).map(subject => (
                                  <Badge key={subject} variant="outline" className="text-xs">
                                    {subject} ({testDetails.stats.subject_breakdown[subject].total})
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </Card>

                        {/* Test-Specific Analytics */}
                        <Card className="p-6 rounded-xl border">
                          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                            <BarChart3 size={18} className="text-primary" />
                            Test Analytics
                          </h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Attempt Status Breakdown */}
                            <div>
                              <h4 className="text-sm font-semibold text-muted-foreground mb-3">ATTEMPT STATUS</h4>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between p-2 rounded bg-green-50 dark:bg-green-900/20">
                                  <span className="text-sm flex items-center gap-2">
                                    <Check size={14} className="text-green-600" />
                                    Attempted & Correct
                                  </span>
                                  <span className="font-bold text-green-600">{testDetails.stats.correct}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 rounded bg-red-50 dark:bg-red-900/20">
                                  <span className="text-sm flex items-center gap-2">
                                    <X size={14} className="text-red-600" />
                                    Attempted & Wrong
                                  </span>
                                  <span className="font-bold text-red-600">{testDetails.stats.wrong}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 rounded bg-gray-50 dark:bg-gray-800">
                                  <span className="text-sm flex items-center gap-2">
                                    <Minus size={14} className="text-gray-600" />
                                    Skipped
                                  </span>
                                  <span className="font-bold text-gray-600">{testDetails.stats.skipped}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 rounded bg-amber-50 dark:bg-amber-900/20">
                                  <span className="text-sm flex items-center gap-2">
                                    <Flag size={14} className="text-amber-600" />
                                    Marked for Review
                                  </span>
                                  <span className="font-bold text-amber-600">
                                    {testDetails.questions.filter(q => q.was_marked_for_review).length}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between p-2 rounded bg-blue-50 dark:bg-blue-900/20">
                                  <span className="text-sm flex items-center gap-2">
                                    <RefreshCw size={14} className="text-blue-600" />
                                    Answer Changed
                                  </span>
                                  <span className="font-bold text-blue-600">
                                    {testDetails.questions.filter(q => q.answer_changed).length}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Time Management */}
                            <div>
                              <h4 className="text-sm font-semibold text-muted-foreground mb-3">TIME MANAGEMENT</h4>
                              <div className="space-y-3">
                                {testDetails.time_taken && (
                                  <>
                                    <div className="p-3 rounded-lg bg-muted/30">
                                      <div className="text-xs text-muted-foreground mb-1">Total Time Taken</div>
                                      <div className="text-2xl font-bold text-foreground">
                                        {Math.floor(testDetails.time_taken / 60)}m {testDetails.time_taken % 60}s
                                      </div>
                                    </div>
                                    <div className="p-3 rounded-lg bg-muted/30">
                                      <div className="text-xs text-muted-foreground mb-1">Average per Question</div>
                                      <div className="text-2xl font-bold text-foreground">
                                        {Math.round(testDetails.time_taken / testDetails.questions.length)}s
                                      </div>
                                    </div>
                                    {testDetails.time_limit_minutes && (
                                      <div className="p-3 rounded-lg bg-muted/30">
                                        <div className="text-xs text-muted-foreground mb-1">Time Remaining</div>
                                        <div className={`text-2xl font-bold ${
                                          (testDetails.time_limit_minutes * 60 - testDetails.time_taken) > 300 ? 'text-green-600' :
                                          (testDetails.time_limit_minutes * 60 - testDetails.time_taken) > 60 ? 'text-yellow-600' : 'text-red-600'
                                        }`}>
                                          {Math.floor((testDetails.time_limit_minutes * 60 - testDetails.time_taken) / 60)}m
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </Card>

                        {/* Subject Breakdown */}
                        {Object.keys(testDetails.stats.subject_breakdown || {}).length > 0 && (
                          <Card className="p-6 rounded-xl border">
                            <h3 className="text-lg font-bold text-foreground mb-4">Subject-wise Performance</h3>
                            <div className="space-y-3">
                              {Object.entries(testDetails.stats.subject_breakdown).map(([subject, stats]) => (
                                <div key={subject} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                  <div className="flex-1">
                                    <div className="font-semibold text-foreground">{subject}</div>
                                    <div className="text-xs text-muted-foreground">
                                      {stats.correct}/{stats.attempted} attempted ({Math.round((stats.correct/stats.attempted)*100) || 0}%)
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className={`text-xl font-bold ${
                                      (stats.correct/stats.attempted*100) >= 80 ? 'text-green-500' : 
                                      (stats.correct/stats.attempted*100) >= 60 ? 'text-yellow-500' : 'text-red-500'
                                    }`}>
                                      {stats.correct}/{stats.total}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Card>
                        )}

                        {/* Questions List */}
                        <Card className="p-6 rounded-xl border">
                          <h3 className="text-lg font-bold text-foreground mb-4">Question-by-Question Review</h3>
                          <div className="space-y-4">
                            {testDetails.questions.map((q, idx) => (
                              <div 
                                key={q.question_id}
                                className={`p-4 rounded-lg border-2 ${
                                  q.is_correct === true ? 'bg-green-50 dark:bg-green-900/10 border-green-200' :
                                  q.is_correct === false ? 'bg-red-50 dark:bg-red-900/10 border-red-200' :
                                  'bg-gray-50 dark:bg-gray-800 border-gray-200'
                                }`}
                              >
                                <div className="flex items-start gap-4">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    q.is_correct === true ? 'bg-green-500 text-white' :
                                    q.is_correct === false ? 'bg-red-500 text-white' :
                                    'bg-gray-400 text-white'
                                  }`}>
                                    {q.is_correct === true ? <Check size={16} /> : 
                                     q.is_correct === false ? <X size={16} /> : 
                                     <span className="text-xs">{idx + 1}</span>}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-4 mb-2">
                                      <div className="flex-1">
                                        <div className="text-sm font-semibold text-foreground mb-1">
                                          Question {idx + 1}
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                          {q.subject_name && <span className="mr-2">📚 {q.subject_name}</span>}
                                          {q.chapter_name && <span className="mr-2">📖 {q.chapter_name}</span>}
                                        </div>
                                      </div>
                                      <div className="flex gap-2 items-center text-xs">
                                        {q.time_spent_seconds && (
                                          <Badge variant="outline" className="gap-1">
                                            <Clock size={10} />
                                            {q.time_spent_seconds}s
                                          </Badge>
                                        )}
                                        {q.answer_changed && <Badge variant="outline">Changed</Badge>}
                                        {q.was_marked_for_review && <Badge variant="outline">Marked</Badge>}
                                      </div>
                                    </div>
                                    <div className="flex gap-4 text-sm">
                                      <div>
                                        <span className="text-muted-foreground">Your Answer: </span>
                                        <span className={`font-semibold ${
                                          q.is_correct === true ? 'text-green-600' : 
                                          q.is_correct === false ? 'text-red-600' : 'text-gray-600'
                                        }`}>
                                          {q.selected_answer || 'Not Attempted'}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">Correct: </span>
                                        <span className="font-semibold text-green-600">{q.correct_answer}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </Card>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* CTA for non-premium */}
        {!basicAnalytics && (
          <Card className="p-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl text-center">
            <Crown className="mx-auto mb-4" size={48} />
            <h3 className="text-2xl font-bold mb-2">Advanced Analytics</h3>
            <p className="text-purple-100 mb-6">
              Get deep insights into your cognitive patterns, predictive scoring, and personalized recommendations
            </p>
            <Button onClick={() => navigate('/subscription')} className="bg-white text-purple-600 hover:bg-purple-100">
              Upgrade to Premium
            </Button>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default AdvancedAnalytics;